import React, { useEffect, useMemo } from 'react'
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
} from '@mui/material'
import { EditUserFormData, AVAILABLE_PERMISSIONS } from '../../types'
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  StyledButton,
  StyledSwitch,
} from '../../styles/StyledComponents'
import { Person as PersonIcon } from '@mui/icons-material'
import { alpha, useTheme } from '@mui/material/styles'
import { OWNER_ROLE_KEY } from '../../../../../../types/rbac/catalogue'
import { useActorPermissionKeys, useSurface } from '../../../../hooks/usePermission'
import { useInviteRoles } from '../../../../hooks/useInviteRoles'
import { useRbacStore } from '../../../../stores/rbacStore'
import { rolePickerChoices, roleKeyOf, roleSendValue } from '../../utils/roleOptions'

interface EditUserDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: () => void
  formData: EditUserFormData
  onFormChange: (formData: EditUserFormData) => void
  userEmail?: string
  /** The company the member belongs to: where the role names and choices come from. */
  companyId?: string | null
  /** The member has no role of their own: the picker shows the default role that applies. */
  roleless?: boolean
}

export const EditUserDialog: React.FC<EditUserDialogProps> = ({
  open,
  onClose,
  onSubmit,
  formData,
  onFormChange,
  userEmail,
  companyId,
  roleless = false,
}) => {
  const theme = useTheme()
  // Changing a role needs roles.assign. Without it the role the form carries is sent back as it
  // was, which cloud treats as no role change (SC-439).
  const canPickRole = useSurface('edit-user-dialog.role-picker')
  const permissions = formData.permissions ?? []

  // One roles request per time the dialog opens (SC-439). Names are the API's, because a platform
  // admin can rename any role. When the list is not there (it failed, or the company is unknown)
  // the catalogue's system roles stand in, so no name is made up here.
  const actorPermissions = useActorPermissionKeys()
  const rolesState = useInviteRoles(companyId, open && canPickRole)
  const catalogue = useRbacStore((s) => s.catalogue)
  const needsCatalogue = open && canPickRole && (!companyId || rolesState.status === 'error')
  useEffect(() => {
    if (needsCatalogue) void useRbacStore.getState().ensureCatalogue()
  }, [needsCatalogue])
  const currentRoleKey = roleKeyOf(formData.role)
  // Cloud's rule for a role change: roles.assign plus every permission of the new role, with no
  // exception for the default role.
  const roleChoices = useMemo(
    () =>
      rolePickerChoices({
        roles: rolesState.roles,
        catalogueRoles: catalogue?.systemRoles,
        actorPermissions,
        currentKey: currentRoleKey,
      }),
    [rolesState.roles, catalogue, actorPermissions, currentRoleKey]
  )
  // `idle` is the render before the request starts; showing the key then would flash a raw key.
  const rolesLoading =
    !!companyId && (rolesState.status === 'loading' || rolesState.status === 'idle')

  const handleInputChange = (field: keyof EditUserFormData, value: unknown) => {
    onFormChange({
      ...formData,
      [field]: value,
    })
  }

  const handlePermissionToggle = (permission: string) => {
    const next = permissions.includes(permission)
      ? permissions.filter((p) => p !== permission)
      : [...permissions, permission]

    onFormChange({
      ...formData,
      permissions: next,
    })
  }

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <StyledDialogTitle>
        <PersonIcon sx={{ color: 'primary.main', fontSize: 24 }} />
        Edit User
      </StyledDialogTitle>
      <StyledDialogContent>
        <Box
          component="form"
          autoComplete="off"
          noValidate
          onSubmit={(e) => e.preventDefault()}
        >
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  bgcolor: 'action.hover',
                  backgroundImage: 'none',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, mb: 0.5 }}>
                  User Information
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Role and profile fields for this company member
                </Typography>
              </Box>
            </Grid>

            {userEmail && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  value={userEmail}
                  margin="dense"
                  disabled
                  name="company-member-email-display"
                  inputProps={{
                    autoComplete: 'off',
                    readOnly: true,
                  }}
                />
              </Grid>
            )}

            {canPickRole && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="dense">
                  <InputLabel id="edit-user-role-label">Role</InputLabel>
                  <Select
                    labelId="edit-user-role-label"
                    value={rolesLoading ? '' : currentRoleKey}
                    onChange={(e) => handleInputChange('role', roleSendValue(String(e.target.value)))}
                    label="Role"
                    name="company-member-role"
                    inputProps={{ autoComplete: 'off' }}
                    // An Owner's role is changed by transferring ownership, never from this list.
                    disabled={rolesLoading || currentRoleKey === OWNER_ROLE_KEY}
                    displayEmpty={rolesLoading}
                    renderValue={
                      rolesLoading
                        ? () => 'Loading roles...'
                        : (value) =>
                            roleChoices.find((c) => c.key === value)?.name ?? String(value)
                    }
                  >
                    {roleChoices.map((choice) => (
                      <MenuItem key={choice.key} value={choice.key} disabled={choice.disabled}>
                        {choice.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/* Hidden with the picker: without roles.assign no role is sent, so the sentence
                    would be false. */}
                {roleless && !rolesLoading && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    This user has no role. The default role will be applied when you save.
                  </Alert>
                )}
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Department"
                value={formData.department ?? ''}
                onChange={(e) => handleInputChange('department', e.target.value)}
                margin="dense"
                name="company-member-department"
                inputProps={{ autoComplete: 'off' }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title ?? ''}
                onChange={(e) => handleInputChange('title', e.target.value)}
                margin="dense"
                name="company-member-title"
                inputProps={{ autoComplete: 'off' }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  mt: 1,
                  mb: 2,
                  p: 1.5,
                  bgcolor: 'action.hover',
                  backgroundImage: 'none',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, mb: 0.5 }}>
                  Permissions
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                  Toggle capabilities for this user
                </Typography>

                <Grid container spacing={1.5}>
                  {AVAILABLE_PERMISSIONS.map((permission) => {
                    const checked = permissions.includes(permission)
                    return (
                      <Grid item xs={12} md={6} key={permission}>
                        <Box
                          data-testid={`edit-user-perm-${permission}`}
                          sx={{
                            p: 1.5,
                            borderRadius: 1,
                            backgroundImage: 'none',
                            bgcolor: checked ? alpha(theme.palette.info.main, 0.1) : 'background.paper',
                            border: '1px solid',
                            borderColor: checked
                              ? alpha(theme.palette.info.main, 0.35)
                              : 'divider',
                            transition: 'background-color 0.15s ease, border-color 0.15s ease',
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: checked
                                ? alpha(theme.palette.info.main, 0.14)
                                : alpha(theme.palette.info.main, 0.06),
                              backgroundImage: 'none',
                              borderColor: alpha(theme.palette.info.main, 0.45),
                            },
                          }}
                          onClick={() => handlePermissionToggle(permission)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              handlePermissionToggle(permission)
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <StyledSwitch
                              checked={checked}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => handlePermissionToggle(permission)}
                              size="small"
                              inputProps={{ 'aria-label': permission }}
                            />
                            <Typography
                              sx={{
                                color: 'text.primary',
                                fontWeight: checked ? 500 : 400,
                                fontSize: '0.875rem',
                              }}
                            >
                              {permission
                                .replace(/([A-Z])/g, ' $1')
                                .replace(/^./, (str) => str.toUpperCase())}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )
                  })}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </StyledDialogContent>
      <StyledDialogActions>
        <StyledButton onClick={onClose} variant="outlined">
          Cancel
        </StyledButton>
        <StyledButton onClick={onSubmit} variant="contained">
          Update User
        </StyledButton>
      </StyledDialogActions>
    </StyledDialog>
  )
}
